<script setup lang="ts">
import { computed, ref } from 'vue';
import { parseISO } from 'date-fns';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { lotFraction, formatQuantity, PERCENT_TONE_CLASSES } from '@/composables/inventory/inventoryTable';
import { useInventoryFormModal } from '@/composables/inventory/useInventoryFormModal';
import { i18n } from '@/plugins/internationalization/i18n';
import { MedicineInventoryFormId, type MedicineLot, type MedicineLotStatus } from '@/types';

const props = defineProps<{
    /** The key's lots, oldest first. */
    lots: MedicineLot[];
    unitName: string;
}>();

const { initialState, stepConfig, header, loadingKey, openResource } = useInventoryFormModal();

const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.lots.${key}`, params);
const heading = (key: string) => t(`headings.${key}`);

const showClosed = ref(false);

/** Lots with something left, in the order the ledger draws from them. */
const openLots = computed(() => props.lots.filter((lot) => lot.status === 'active' || lot.status === 'waiting'));
/** Spent or reset lots, newest first. */
const closedLots = computed(() => props.lots.filter((lot) => lot.status === 'used' || lot.status === 'reset').reverse());
const visibleLots = computed(() => (showClosed.value ? [...openLots.value, ...closedLots.value] : openLots.value));

const STATUS_CLASSES: Record<MedicineLotStatus, string> = {
    active: PERCENT_TONE_CLASSES.ok,
    waiting: 'border-transparent bg-gray-100 text-gray-700',
    used: 'border-transparent bg-transparent text-gray-500',
    reset: PERCENT_TONE_CLASSES.warning,
};

const formatDate = (date: string) => i18n.global.d(parseISO(date), 'short');
const withUnit = (value: number) => `${formatQuantity(value)} ${props.unitName}`;

/** "2 × 500 ml" for a purchase made in packs, otherwise just what kind of entry it is. */
function postOf(lot: MedicineLot): string {
    if (lot.source === 'count') {
        return t('post.count');
    }

    if (lot.packs !== null && lot.pack_size !== null) {
        return t('post.packs', { packs: formatQuantity(lot.packs), size: formatQuantity(lot.pack_size), unit: props.unitName });
    }

    return t('post.purchase');
}

function keyOf(lot: MedicineLot): string {
    return `${lot.source}-${lot.record_id}`;
}

const isLoading = computed(() => loadingKey.value !== null);

/** A lot opens the purchase or count it began with. */
function openLot(lot: MedicineLot, open: CallableFunction) {
    if (isLoading.value) {
        return;
    }

    openResource(keyOf(lot), lot.source === 'purchase' ? MedicineInventoryFormId.Purchase : MedicineInventoryFormId.Count, lot.record_id, open);
}
</script>

<template>
    <MainViewCard class="overflow-hidden">
        <div class="px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4">
            <h2 class="text-lg font-medium">{{ t('heading') }}</h2>
            <Button v-if="closedLots.length" variant="ghost" size="sm" @click="showClosed = !showClosed">
                {{ showClosed ? t('hideClosed') : t('showClosed', { count: closedLots.length }) }}
            </Button>
        </div>
        <!-- A save or delete reloads the inventory queries through `useClearCachesOnFormSubmission`, so the modal needs no close handler here. -->
        <ActionModal :initial-step-index="1" :initial-state="initialState" :step-config="stepConfig" :header="header">
            <template #default="{ open }">
                <div v-if="lots.length === 0" class="py-10 px-4 text-center text-gray-500">{{ t('empty') }}</div>
                <div v-else-if="visibleLots.length === 0" class="px-4 pb-6 text-gray-500">{{ t('noOpen') }}</div>
                <div v-else class="overflow-x-auto">
                    <table class="w-full text-normal">
                        <thead>
                            <tr class="border-y border-gray-200 bg-gray-50 text-gray-500">
                                <th class="px-4 py-2 text-left font-medium">{{ heading('date') }}</th>
                                <th class="px-2 py-2 text-left font-medium">{{ heading('post') }}</th>
                                <th class="px-2 py-2 text-right font-medium">{{ heading('quantity') }}</th>
                                <th class="px-2 py-2 text-right font-medium">{{ heading('used') }}</th>
                                <th class="px-2 py-2 text-right font-medium">{{ heading('reset') }}</th>
                                <th class="px-2 py-2 text-right font-medium">{{ heading('remaining') }}</th>
                                <th class="px-2 py-2 text-left font-medium">{{ heading('status') }}</th>
                                <th class="px-4 py-2 text-left font-medium">{{ heading('batchNumber') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="lot in visibleLots"
                                :key="keyOf(lot)"
                                class="border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                                :class="isLoading ? 'cursor-wait' : 'cursor-pointer'"
                                @click="openLot(lot, open)"
                            >
                                <!-- The button is what keyboards and screen readers open; a click anywhere else on the row does the same. -->
                                <td class="px-4 py-2">
                                    <button
                                        type="button"
                                        class="whitespace-nowrap rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                                        @click.stop="openLot(lot, open)"
                                    >
                                        {{ formatDate(lot.date) }}
                                    </button>
                                </td>
                                <td class="px-2 py-2 whitespace-nowrap">{{ postOf(lot) }}</td>
                                <td class="px-2 py-2 text-right whitespace-nowrap tabular-nums">{{ withUnit(lot.quantity) }}</td>
                                <td class="px-2 py-2 text-right whitespace-nowrap tabular-nums">{{ withUnit(lot.used) }}</td>
                                <td class="px-2 py-2 text-right whitespace-nowrap tabular-nums">
                                    <span v-if="lot.reset > 0" class="text-red-600">−{{ withUnit(lot.reset) }}</span>
                                    <span v-else class="text-gray-400" aria-hidden="true">–</span>
                                </td>
                                <td class="px-2 py-2 text-right">
                                    <div class="flex flex-col items-end gap-1">
                                        <span class="whitespace-nowrap font-semibold tabular-nums">{{ withUnit(lot.remaining) }}</span>
                                        <div class="h-1.5 w-16 overflow-hidden rounded bg-gray-200">
                                            <div class="h-full rounded bg-primary-700" :style="{ width: `${lotFraction(lot) * 100}%` }"></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-2 py-2">
                                    <Badge variant="outline" class="whitespace-nowrap font-medium" :class="STATUS_CLASSES[lot.status]">{{ t(`status.${lot.status}`) }}</Badge>
                                </td>
                                <td class="px-4 py-2 whitespace-nowrap">{{ lot.batch_number ?? '–' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </ActionModal>
    </MainViewCard>
</template>
